import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader
} from 'reactstrap';
import { toast } from 'react-toastify';
import PageHeader from '../common/PageHeader';
import ActionConfirmModal from '../common/ActionConfirmModal';
import TablePageSizeSelect from '../common/TablePageSizeSelect';
import MedDataTable from '../common/MedDataTable';
import apiBase from '../../apiConfig';
import useStoredTablePageSize from '../../hooks/useStoredTablePageSize';

const initialForm = {
  name: '',
  sort: 1,
  is_active: 1
};

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [pageSize, setPageSize] = useStoredTablePageSize();
  const [primaryRoles, setPrimaryRoles] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchRoles = async () => {
    const res = await axios.get(`${apiBase}/roles.php`);
    if (res.data.success) {
      setRoles(res.data.roles);
      setPrimaryRoles(res.data.primary_roles || []);
    }
  };

  useEffect(() => {
    fetchRoles().catch(console.error);
  }, []);

  const columns = [
    {
      dataField: 'name',
      text: 'Rol',
      sort: true,
      classes: 'align-middle',
      formatter: (value, role) => (
        <>
          <div className="font-weight-bold">{value}</div>
          <div className="fs--2 text-500">{role.permissions.slice(0, 4).map(item => item.module).join(', ') || 'permission yo‘q'}</div>
        </>
      )
    },
    { dataField: 'sort', text: 'Tartib', sort: true, classes: 'align-middle' },
    { dataField: 'permission_count', text: 'Permission', sort: true, classes: 'align-middle' },
    {
      dataField: 'is_active',
      text: 'Holat',
      sort: true,
      classes: 'align-middle',
      formatter: value => <Badge color={value ? 'soft-success' : 'soft-secondary'}>{value ? 'Faol' : 'Nofaol'}</Badge>
    },
    {
      dataField: 'actions',
      text: 'Amallar',
      isDummyField: true,
      headerStyle: { width: '6.5rem' },
      classes: 'align-middle',
      formatter: (data, role) => (
        <div className="med-actions-row">
          <Button color="link" className="p-0 med-action-btn" onClick={() => openEdit(role)}>
            <FontAwesomeIcon icon="pencil-alt" />
          </Button>
          <Button
            color="link"
            className="p-0 text-danger med-action-btn med-action-btn-danger"
            onClick={() => setDeleteTarget({ id: role.id, name: role.name })}
          >
            <FontAwesomeIcon icon="trash-alt" />
          </Button>
        </div>
      )
    }
  ];

  const openCreate = () => {
    setEditingId(null);
    setForm(initialForm);
    setModalOpen(true);
  };

  const openEdit = role => {
    setEditingId(role.id);
    setForm({
      name: role.name,
      sort: role.sort,
      is_active: role.is_active ? 1 : 0
    });
    setModalOpen(true);
  };

  const handleSubmit = async e => {
    e.preventDefault();

    try {
      if (editingId) {
        const res = await axios.put(`${apiBase}/roles.php`, { ...form, id: editingId });
        if (res.data.success) toast.success(res.data.message);
      } else {
        const res = await axios.post(`${apiBase}/roles.php`, form);
        if (res.data.success) toast.success(res.data.message);
      }

      setModalOpen(false);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Rol saqlanmadi');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await axios.delete(`${apiBase}/roles.php`, { data: { id: deleteTarget.id } });
      if (res.data.success) {
        toast.success(res.data.message);
        fetchRoles();
        setDeleteTarget(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Rol o‘chirilmadi');
    }
  };

  return (
    <>
      <PageHeader title="Rollar" description="Eski tizimdagi access modeli asosida rollarni boshqarish." className="mb-3" />
      <Card className="border-0 shadow-sm med-dashboard-card mb-3">
        <CardBody className="med-dashboard-card-body">
          <h5 className="mb-3">5 ta asosiy rol</h5>
          <div className="d-flex flex-wrap">
            {primaryRoles.map(role => (
              <Badge key={role.id} color="soft-primary" pill className="mr-2 mb-2 px-3 py-2">
                {role.name}
              </Badge>
            ))}
          </div>
        </CardBody>
      </Card>
      <Card className="border-0 shadow-sm med-dashboard-card">
        <CardBody className="med-dashboard-card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Rollar ro'yxati</h5>
            <div className="d-flex align-items-center">
              <TablePageSizeSelect value={pageSize} onChange={setPageSize} className="med-table-page-size mr-3" />
              <Button color="outline-primary" className="med-outline-add-btn" onClick={openCreate}>
                <FontAwesomeIcon icon="plus" className="mr-2" />
                Yangi rol
              </Button>
            </div>
          </div>
          <MedDataTable
            data={roles}
            columns={columns}
            pageSize={pageSize}
            paginationResetKey="roles"
            noDataIndication={<div className="text-center py-4 text-500">Rollar topilmadi</div>}
          />
        </CardBody>
      </Card>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} centered>
        <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
          {editingId ? 'Rolni tahrirlash' : 'Yangi rol'}
        </ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody>
            <FormGroup>
              <Label>Rol nomi</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </FormGroup>
            <FormGroup>
              <Label>Tartib</Label>
              <Input type="number" value={form.sort} onChange={e => setForm({ ...form, sort: e.target.value })} />
            </FormGroup>
            <FormGroup check>
              <Label check>
                <Input
                  type="checkbox"
                  checked={Number(form.is_active) === 1}
                  onChange={e => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })}
                />
                Faol holatda saqlash
              </Label>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setModalOpen(false)}>
              <FontAwesomeIcon icon="times" className="mr-2" />
              Bekor qilish
            </Button>
            <Button color="primary" type="submit">
              <FontAwesomeIcon icon="check-circle" className="mr-2" />
              Saqlash
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <ActionConfirmModal
        isOpen={Boolean(deleteTarget)}
        toggle={() => setDeleteTarget(null)}
        title="Rolni nofaol qilish"
        message={deleteTarget ? `${deleteTarget.name} rolini nofaol qilishni tasdiqlaysizmi?` : ''}
        confirmLabel="Nofaol qilish"
        onConfirm={handleDelete}
      />
    </>
  );
};

export default Roles;
